import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { CreditCard, Download, Calendar, AlertCircle, ExternalLink } from "lucide-react";
import { format } from "date-fns";
import { sk } from "date-fns/locale";
import { formatCurrency } from "@/lib/currency";

interface UserSubscription {
  id: number;
  status: 'trial' | 'active' | 'past_due' | 'cancelled' | 'paused';
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  subscriptionValidUntil: string | null;
  trialEnd: string | null;
  nextBillingDate: string | null;
  cancelAtPeriodEnd: boolean;
  cancelledAt: string | null;
  lastPaymentDate: string | null;
  totalPaid: string | null;
}

interface SubscriptionPlan {
  id: number;
  name: string;
  price: number;
  currency: string;
  billingInterval: 'monthly' | 'yearly';
  features: string[];
}

interface Payment {
  id: number;
  amount: number;
  currency: string;
  invoiceUrl: string | null;
  paidAt: string;
  status: string;
}

interface BillingData {
  subscription: UserSubscription | null;
  plan: SubscriptionPlan | null;
  payments: Payment[];
}

export function BillingSection() {
  const { toast } = useToast();

  const handleExportInvoices = async () => {
    try {
      const response = await fetch('/api/export/invoices', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/zip',
        },
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Nie ste prihlásený');
        }
        throw new Error('Export sa nepodaril');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `faktury-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: "Export dokončený",
        description: "Faktúry boli exportované ako ZIP súbor",
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Chyba exportu",
        description: error instanceof Error ? error.message : "Nepodarilo sa exportovať faktúry",
        variant: "default",
      });
    }
  };

  const handleExportTasks = async () => {
    try {
      const response = await fetch('/api/export/tasks', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'text/csv',
        },
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Nie ste prihlásený');
        }
        throw new Error('Export sa nepodaril');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `ulohy-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: "Export dokončený",
        description: "Úlohy boli exportované ako CSV súbor",
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Chyba exportu",
        description: error instanceof Error ? error.message : "Nepodarilo sa exportovať úlohy",
        variant: "default",
      });
    }
  };

  const handleExportClients = async () => {
    try {
      const response = await fetch('/api/export/clients', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'text/csv',
        },
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Nie ste prihlásený');
        }
        throw new Error('Export sa nepodaril');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `klienti-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: "Export dokončený",
        description: "Klienti boli exportovaní ako CSV súbor",
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Chyba exportu",
        description: error instanceof Error ? error.message : "Nepodarilo sa exportovať klientov",
        variant: "default",
      });
    }
  };

  const { data: billingData, isLoading, error } = useQuery<BillingData>({
    queryKey: ['/api/billing'],
    staleTime: 1000 * 60 * 5, // 5 minút
  });

  const cancelSubscriptionMutation = useMutation({
    mutationFn: async (cancelAtPeriodEnd: boolean = true) => {
      const res = await apiRequest('POST', '/api/billing/cancel-subscription', { cancelAtPeriodEnd });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/billing'] });
      toast({
        title: "Predplatné zrušené",
        description: "Vaše predplatné bude zrušené na konci aktuálneho obdobia.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Informácia",
        description: error.message || "Nepodarilo sa zrušiť predplatné",
        variant: "default",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-lg mb-4"></div>
          <div className="h-64 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center space-x-2 text-red-600">
            <AlertCircle className="h-5 w-5" />
            <span>Nepodarilo sa načítať fakturačné údaje</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { subscription, plan, payments } = billingData || {};

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default" className="bg-green-100 text-green-800">Aktívne</Badge>;
      case 'trial':
        return <Badge variant="secondary">Skúšobné období</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Zrušené</Badge>;
      case 'past_due':
        return <Badge variant="destructive">Nedoplatené</Badge>;
      case 'paused':
        return <Badge variant="secondary">Pozastavené</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'dd.MM.yyyy', { locale: sk });
  };

  // formatCurrency now imported from centralized utility - modified to use currency parameter

  return (
    <div className="space-y-6">
      {/* Aktuálne predplatné */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CreditCard className="h-5 w-5" />
            <span>Aktuálne predplatné</span>
          </CardTitle>
          <CardDescription>
            Prehľad vášho aktuálneho predplatného a plánu
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {subscription && plan ? (
            <>
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold">{plan.name}</h3>
                  <p className="text-sm text-gray-600">
                    {formatCurrency(plan.price, plan.currency as 'EUR' | 'USD')} / {plan.billingInterval === 'monthly' ? 'mesiac' : 'rok'}
                  </p>
                </div>
                <div className="text-right">
                  {getStatusBadge(subscription.status)}
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Status</p>
                  <div className="text-sm">{getStatusBadge(subscription.status)}</div>
                </div>
                
                {subscription.nextBillingDate && (
                  <div>
                    <p className="text-sm font-medium text-gray-500">Ďalšia platba</p>
                    <p className="text-sm">{formatDate(subscription.nextBillingDate)}</p>
                  </div>
                )}

                {subscription.currentPeriodEnd && (
                  <div>
                    <p className="text-sm font-medium text-gray-500">Platné do</p>
                    <p className="text-sm">{formatDate(subscription.currentPeriodEnd)}</p>
                  </div>
                )}

                {subscription.trialEnd && (
                  <div>
                    <p className="text-sm font-medium text-gray-500">Skúšobné obdobie do</p>
                    <p className="text-sm">{formatDate(subscription.trialEnd)}</p>
                  </div>
                )}

                {subscription.totalPaid && (
                  <div>
                    <p className="text-sm font-medium text-gray-500">Celkom zaplatené</p>
                    <p className="text-sm">{formatCurrency(parseFloat(subscription.totalPaid), plan.currency as 'EUR' | 'USD')}</p>
                  </div>
                )}
              </div>

              {subscription.status === 'active' && !subscription.cancelAtPeriodEnd && (
                <div className="pt-4">
                  <Button
                    variant="outline"
                    onClick={() => cancelSubscriptionMutation.mutate(true)}
                    disabled={cancelSubscriptionMutation.isPending}
                  >
                    {cancelSubscriptionMutation.isPending ? 'Ruší sa...' : 'Zrušiť predplatné'}
                  </Button>
                  <p className="text-xs text-gray-500 mt-2">
                    Predplatné zostane aktívne do konca aktuálneho obdobia
                  </p>
                </div>
              )}

              {subscription.cancelAtPeriodEnd && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-4 w-4 text-yellow-600" />
                    <p className="text-sm text-yellow-800">
                      Predplatné bude zrušené {formatDate(subscription.subscriptionValidUntil)}
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">Momentálne nemáte aktívne predplatné</p>
              <Button asChild>
                <a href="/subscribe">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Vybrať plán
                </a>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* História platieb */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>História platieb</span>
          </CardTitle>
          <CardDescription>
            Všetky vaše platby a faktúry
          </CardDescription>
        </CardHeader>
        <CardContent>
      {payments && payments.length > 0 ? (
            <div className="space-y-4">
              {payments.map((payment) => (
                <div key={payment.id} className="flex justify-between items-center p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">
                      {formatCurrency(payment.amount, payment.currency as 'EUR' | 'USD')}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatDate(payment.paidAt)}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="bg-green-50 text-green-700">
                      {payment.status === 'completed' ? 'Zaplatené' : payment.status}
                    </Badge>
                    {payment.invoiceUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                      >
                        <a href={payment.invoiceUrl} target="_blank" rel="noopener noreferrer">
                          <Download className="h-4 w-4 mr-1" />
                          Faktúra
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">Zatiaľ nemáte žiadne platby</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}